import { useCallback, useMemo, useRef, useState } from "react";

import {
  BasicRichText,
  type BasicRichTextProps,
  type RemirrorMenuItem,
} from "./BasicRichText";
import type { RemirrorProps } from "./Remirror";
import { getMentions } from "./getMentions";
import type { ChooserState } from "./presets/menuTypes";
import { presets } from "./presets/presets";
import type { RemirrorMention } from "./ui/Mentions/useMention";

export type RichTextProps = {
  forType?: string;
  forId?: string;
  preset?: keyof typeof presets;
  // If preset === "custom", these extensions will be used to filter the full preset options
  customPresetExtensions?: (typeof presets)["custom"]["options"][number]["name"][];
  customPresetMenu?: (typeof presets)["custom"]["options"][number]["name"][];
  extraData?: unknown;
  fullMentionLinks?: boolean;
  /** Passed through to Remirror (e.g. `onExternalLink` for embedded WebView bridges). */
  remirrorSettings?: RemirrorProps["settings"];
  onChange?: (html: string, textValue: string, extraData: unknown) => void;
} & BasicRichTextProps;

// forType and forId are required for uploads, however in cases such as the "simple" preset no upload is possible therefore they don't need to be added
// This is currently not enforced by the type system but can be gathered by the various examples of us using this component across the app
export const RichText = ({
  value,
  forType,
  forId,
  preset = "full",
  customPresetExtensions,
  customPresetMenu,
  onChange,
  extraData,
  fullMentionLinks,
  remirrorSettings,
  ...rest
}: RichTextProps) => {
  const [mentionState, setMentionState] = useState<{
    mentions: Record<string, Array<RemirrorMention>>;
    lastKind: string;
    lastQuery: string;
  } | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getThrottledMentions = useCallback((kind: string[], query: string) => {
    const kindKey = kind[0];
    if (!kindKey) return;
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }

    timeout.current = setTimeout(() => {
      getMentions(kind, query)
        .then((result) => {
          setMentionState({
            lastKind: kindKey,
            lastQuery: query,
            mentions: result as Record<string, Array<RemirrorMention>>,
          });
          timeout.current = null;
        })
        .catch(() => {});
    }, 300);
  }, []);

  const [chooser, setChooser] = useState<ChooserState>({});

  const setChooserType = useCallback(
    (kind: string, open: boolean, cmd?: unknown, text?: string) =>
      setChooser(() => ({ [kind]: { open, cmd, text } })),
    [],
  );

  const handleChange = useCallback(
    (html: string, textValue: string) => {
      if (onChange) {
        return onChange(html, textValue, extraData);
      }
    },
    [onChange, extraData],
  );

  const presetChoice = useMemo(() => presets[preset], [preset]);
  const initialPresetMenu = useMemo(
    () => presetChoice.getMenu(chooser, setChooserType, forType, forId),
    [presetChoice, chooser, setChooserType, forType, forId],
  );

  const [presetOptions, presetMenu] = useMemo(() => {
    let presetOptions = presetChoice.options;
    let presetMenu = initialPresetMenu;
    if (preset === "custom" && customPresetExtensions) {
      presetOptions = presetOptions.filter((option) =>
        customPresetExtensions.includes(option.name),
      );
      presetMenu = (presetMenu as RemirrorMenuItem[]).filter((menu) =>
        (customPresetMenu || customPresetExtensions).includes(menu.name),
      );
    }
    return [presetOptions, presetMenu];
  }, [
    preset,
    presetChoice,
    customPresetExtensions,
    customPresetMenu,
    initialPresetMenu,
  ]);

  return (
    <BasicRichText
      {...rest}
      value={value}
      options={presetOptions}
      mentions={mentionState?.mentions ?? {}}
      onSuggest={getThrottledMentions}
      extraMenuItems={presetMenu as RemirrorMenuItem[]}
      onChange={handleChange}
      settings={{
        ...(remirrorSettings || {}),
        ...(forType && forId ? { uploadOptions: { forType, forId } } : {}),
        fullMentionLinks: !!fullMentionLinks,
      }}
    />
  );
};

export const RichTextViewer = (
  props: Omit<BasicRichTextProps, "readOnly"> & {
    renderMention?:
      | ((args: {
          kind: string;
          id: string;
          label?: string;
          attrs: Record<string, unknown>;
        }) => React.ReactNode)
      | undefined;
  },
) => {
  return (
    <BasicRichText
      options={presets.full.options}
      {...props}
      readOnly={true}
      settings={{
        ...(props.settings || {}),
        renderMention: props.renderMention,
      }}
    />
  );
};

export const MarkdownViewer = (props: Omit<BasicRichTextProps, "readOnly">) => {
  return <RichTextViewer {...props} markdown={true} emptyReturnValue="" />;
};
